import { ICellRendererParams } from 'ag-grid-community'
import columnRenderers, { PLACEHOLDER } from 'components/column-renderers'
import dateRenderer from 'components/column-renderers/date'
import { notificationSettingsCellRenderer } from 'components/column-renderers/notificationSettings'
import { getActionColumn } from 'features/UI'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { routes } from 'routes/routes'
import { OwnedAccessCode } from 'types'
import { getRoute } from 'utils'
import { TraineeStatusBadge } from '../components/TraineeStatusBadge/TraineeStatusBadge'
import { UserOrgAccessCodesCell } from '../components/UserOrgAccessCodesCell/UserOrgAccessCodesCell'

export function useUserOrgTraineeColumns() {
  const navigate = useNavigate()

  const handleEditUser = useCallback(
    (userId: number) => {
      const route = getRoute(routes.orgUserTraineeDetails, { userId })
      navigate(route)
    },
    [navigate],
  )

  const actions = useMemo(
    () => [
      {
        name: 'Edit',
        onClick: handleEditUser,
      },
    ],
    [handleEditUser],
  )

  return useMemo(
    () => [
      { headerName: 'First Name', field: 'firstname', filter: 'agTextColumnFilter' },
      { headerName: 'Last Name', field: 'lastname', filter: 'agTextColumnFilter' },
      { headerName: 'Total Time Online', field: 'totalEngagement' },
      { headerName: 'Email', field: 'email', filter: 'agTextColumnFilter' },
      {
        headerName: 'User Status',
        field: 'userStatus',
        cellRenderer: (props: ICellRendererParams<{ userStatus?: string }>) => (
          <TraineeStatusBadge status={props.data?.userStatus} />
        ),
      },
      {
        headerName: 'ORG/State/UNIT',
        field: 'organizationName',
        cellRenderer: columnRenderers.accessCodeRenderer,
      },
      {
        headerName: 'Institutional Access Code',
        field: 'accessCodeCode',
        cellRenderer: (
          props: ICellRendererParams<{
            accessCodeCode: string
            accessCodeHistory: OwnedAccessCode[]
          }>,
        ) => {
          if (!props.data) return PLACEHOLDER

          const { accessCodeCode, accessCodeHistory } = props.data
          if (accessCodeHistory && accessCodeHistory.length > 0) {
            return (
              <UserOrgAccessCodesCell
                accessCodes={accessCodeHistory}
                showOrgName
                showNumbersOfUsers={false}
              />
            )
          }

          return accessCodeCode || PLACEHOLDER
        },
      },
      { headerName: 'Pathway', field: 'trainingPathwayTypeTitle' },
      { headerName: 'Access Code Expire Date', field: 'accessCodeExpiryDate', minWidth: 260 },
      {
        headerName: 'Access Code Effective Date',
        cellRenderer: dateRenderer('accessCodeEffectiveDate'),
        field: 'accessCodeEffectiveDate',
        colId: 'accessCodeEffectiveDate',
        minWidth: 260,
      },
      {
        headerName: 'Last Notification',
        field: 'lastNotificationSentAt',
        cellRenderer: dateRenderer('lastNotificationSentAt', 'MM/DD/YY'),
        minWidth: 200,
      },
      {
        headerName: 'Notification Settings',
        field: 'notificationDelivery',
        cellRenderer: notificationSettingsCellRenderer,
        minWidth: 280,
      },
      getActionColumn(actions),
    ],
    [actions],
  )
}
